import { Box, Button, List, ListItemButton, Typography } from '@mui/material';

import { useLocale } from '@shared/hooks/locale';

import { formatReleaseDay } from './releaseDate';
import type { LocalizedRelease } from './releaseEntries';
import { TopicIcon } from './TopicIcon';

// Shown in the bell. Only the latest entries: the full history is the
// /whats-new page.
export const WHATS_NEW_BELL_ITEMS = 5;

type Props = {
  releases: readonly LocalizedRelease[];
  // Unread when the popover opened: they stay highlighted while it is open
  // even though opening the tab already marked them as seen.
  newIds: ReadonlySet<string>;
  onOpen: (release: LocalizedRelease) => void;
  onSeeAll: () => void;
};

export const WhatsNewList = ({ releases, newIds, onOpen, onSeeAll }: Props) => {
  const { t, dateLocale } = useLocale();

  if (releases.length === 0) {
    return (
      <Typography
        sx={{ px: 2, py: 5, textAlign: 'center', color: 'text.secondary' }}
      >
        {t('No product updates yet.')}
      </Typography>
    );
  }

  return (
    <>
      <List dense disablePadding>
        {releases.slice(0, WHATS_NEW_BELL_ITEMS).map(release => {
          const isNew = newIds.has(release.id);
          return (
            <ListItemButton
              key={release.id}
              onClick={() => onOpen(release)}
              sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.25 }}
            >
              <Box sx={{ pt: '2px', opacity: isNew ? 1 : 0.75 }}>
                <TopicIcon topic={release.meta.topic} size={28} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: isNew ? 600 : 400 }}
                >
                  {release.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {release.summary}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {formatReleaseDay(release.meta.date, dateLocale)}
                </Typography>
              </Box>
              {isNew && (
                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    mt: '2px',
                    px: 0.75,
                    borderRadius: 1,
                    fontWeight: 600,
                    color: 'primary.contrastText',
                    bgcolor: 'primary.main',
                  }}
                >
                  {t('New')}
                </Typography>
              )}
            </ListItemButton>
          );
        })}
      </List>
      <Box
        sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button size="small" fullWidth onClick={onSeeAll}>
          {t('See all updates')}
        </Button>
      </Box>
    </>
  );
};
